/**
 * Progress photos on body measurements, for the website and the apps: how
 * many an entry holds, how a photo is prepared before it uploads, who may add
 * one, who can see them, and the words each of those states says.
 *
 * The API's rules this mirrors (progress-photos handoff):
 * - adding photos needs the `measurement_photos` plan feature (402), then
 *   `features.media_uploads` on the signed-in user (403 TWO_FACTOR_REQUIRED);
 * - an entry holds at most four photos (422 on `media`);
 * - removing a saved photo needs neither, and can't be undone.
 *
 * Data-only, so React Native can read it. Import it by subpath
 * (`shared/src/config/progress-photos.config`) on mobile.
 */

import { PRIVACY_GROUPS } from './settings.config';
import { planNameToTier, planRank } from './subscription-plans';

import type { SubscriptionTier, User } from '../types/auth';

// =============================================================================
// LIMITS
// =============================================================================

/** The most photos one entry holds. The API refuses a fifth with a 422. */
export const MAX_PROGRESS_PHOTOS = 4;

/**
 * The longest edge, in pixels, a photo is shrunk to before it uploads. Re-saved
 * as a JPEG at this size, a phone photo lands well under the API's ~5 MB cap,
 * and loses its EXIF data (location included) on the way.
 */
export const PROGRESS_PHOTO_MAX_EDGE = 2048;

/** JPEG quality for the re-saved photo, 0–1. */
export const PROGRESS_PHOTO_JPEG_QUALITY = 0.85;

/** What the API accepts on `POST /file/upload`: JPEG and PNG only. */
export const PROGRESS_PHOTO_UPLOAD_TYPES = ['image/jpeg', 'image/png'] as const;

/**
 * What the picker offers. Wider than the upload types: HEIC and WebP are
 * converted to JPEG on the device where it can open them.
 */
export const PROGRESS_PHOTO_PICK_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
] as const;

/** The lowest plan that includes progress photos. */
export const PROGRESS_PHOTOS_REQUIRED_TIER: SubscriptionTier = 'pro';

// =============================================================================
// WHO MAY ADD PHOTOS
// =============================================================================

/**
 * Why the signed-in user can't add a photo, in the API's own order:
 * - `plan`: the plan doesn't include progress photos (the API's 402).
 * - `two-factor`: two-factor is off on an account that signs in with a
 *   password (the API's 403). A Google or Apple account qualifies without it.
 *
 * Saved photos stay visible and removable under either lock.
 */
export type ProgressPhotoLock = 'plan' | 'two-factor';

/**
 * Read the lock off the signed-in user, or `null` when they can add photos.
 *
 * An account with no `subscription` is read as Free and one with no
 * `features` as locked, so a photo is never picked only to be refused.
 */
export function progressPhotoLock(
  user: Pick<User, 'subscription' | 'features'> | null | undefined
): ProgressPhotoLock | null {
  const tier = user?.subscription
    ? planNameToTier(user.subscription.plan)
    : 'free';

  if (planRank(tier) < planRank(PROGRESS_PHOTOS_REQUIRED_TIER)) return 'plan';
  if (user?.features?.media_uploads !== true) return 'two-factor';

  return null;
}

/** What each lock says, and where its link goes (the caller owns the route). */
export const PROGRESS_PHOTO_LOCKS: Record<
  ProgressPhotoLock,
  { title: string; description: string; action: string }
> = {
  plan: {
    title: 'Progress photos come with Pro and Premium',
    description: 'Up to 4 per entry, private unless you share them.',
    action: 'Compare plans',
  },
  'two-factor': {
    // The API's own sentence for its 403, so the lock and a refusal agree.
    title: 'Turn on two-factor to add progress photos',
    description:
      'It keeps your photos private, even if someone learns your password.',
    action: 'Turn on two-factor',
  },
};

// =============================================================================
// WHO CAN SEE THEM
// =============================================================================

/** Who besides the owner can see their progress photos. */
export type ProgressPhotoAudience =
  'only-you' | 'trainer' | 'friends' | 'trainer-and-friends';

/** A privacy key's value, falling back to its default in Settings. */
function readPrivacyKey(
  settings: Record<string, string | boolean> | undefined,
  key: string
): boolean {
  const value = settings?.[key];
  if (typeof value === 'boolean') return value;

  for (const group of PRIVACY_GROUPS) {
    const item = group.items.find((candidate) => candidate.key === key);
    if (item) return item.defaultValue === true;
  }

  return false;
}

/**
 * Who can see the owner's progress photos, read from their own privacy
 * settings. The API opens photos only alongside measurements: a trainer needs
 * both `trainer_view_measurements` and `trainer_view_progress_photos`, a
 * friend both `show_measurements` and `show_progress_photos`.
 */
export function progressPhotoAudience(
  privacySettings: Record<string, string | boolean> | undefined
): ProgressPhotoAudience {
  const trainer =
    readPrivacyKey(privacySettings, 'trainer_view_measurements') &&
    readPrivacyKey(privacySettings, 'trainer_view_progress_photos');
  const friends =
    readPrivacyKey(privacySettings, 'show_measurements') &&
    readPrivacyKey(privacySettings, 'show_progress_photos');

  if (trainer && friends) return 'trainer-and-friends';
  if (trainer) return 'trainer';
  if (friends) return 'friends';
  return 'only-you';
}

/** The privacy line under the photos, one per audience. */
export const PROGRESS_PHOTO_AUDIENCE_LINES: Record<
  ProgressPhotoAudience,
  string
> = {
  'only-you': 'Only you can see these',
  trainer: 'You and your trainer can see these',
  friends: 'You and your friends can see these',
  'trainer-and-friends': 'You, your trainer and your friends can see these',
};

/** The same audience as a short fact, for an entry's "Photos seen by" row. */
export const PROGRESS_PHOTO_AUDIENCE_FACTS: Record<
  ProgressPhotoAudience,
  string
> = {
  'only-you': 'Only you',
  trainer: 'You and your trainer',
  friends: 'You and your friends',
  'trainer-and-friends': 'You, your trainer and friends',
};

// =============================================================================
// WORDS
// =============================================================================

export const PROGRESS_PHOTO_COPY = {
  sectionLabel: 'Progress photos',
  addTitle: 'Add progress photos',
  addOne: 'Add a photo',
  addHint: 'Up to 4 per entry — front, back and both sides',
  /** The confirm before a saved photo is deleted. The API has no undo. */
  deleteTitle: 'Delete this photo?',
  deleteBody:
    'It’s deleted now, not when you save. To get it back, upload it again.',
  deleteFromEntryBody:
    'It’s deleted for good. To get it back, upload it again.',
  deleteConfirm: 'Delete photo',
  /** A trainer's empty state: true whether the client took none or hides them. */
  noneShared: 'No photos shared',
  noneOnEntry: 'No photos on this entry',
  /** The refusal fallback: two-factor went off elsewhere while the form sat open. */
  refusedTitle: 'Nothing was saved',
  refusedBody:
    'Two-factor was turned off on another device, so these photos can’t be added.',
  saveWithoutPhotos: 'Save without photos',
} as const;

/** "1 photo didn't upload. Retry it or remove it." */
export function failedUploadsNote(count: number): string {
  return count === 1
    ? '1 photo didn’t upload. Retry it or remove it.'
    : `${count} photos didn’t upload. Retry them or remove them.`;
}

/** "IMG_2041.HEIC couldn't be opened in this browser. Try a JPEG or PNG." */
export function unreadablePhotoNote(fileName: string): string {
  return `${fileName} couldn’t be opened here. Try a JPEG or PNG.`;
}

/** "Only 4 photos fit on an entry. 2 weren't added." */
export function photosLeftOutNote(count: number): string {
  return `Only ${MAX_PROGRESS_PHOTOS} photos fit on an entry. ${count} ${
    count === 1 ? 'wasn’t' : 'weren’t'
  } added.`;
}
